import { useGlobalContext } from '@/context/Global/GlobalContext';
import { useUpdateDeliverySettings } from '@/hooks/order/details/useUpdateDeliverySettings';
import { useGetOrderDetails } from '@/hooks/order/useGetOrderDetails';
import { Dayjs } from 'dayjs';
import { WritableDraft } from 'immer';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { orderDetailsResponseToOrderDeliveryDetail } from '../mapper';
import { DeliveryDetailUi, initialState } from './OrderDeliveryDetailUI';

export function useOrderDeliveryDetailViewController() {
  const [uiState, setUiState] = useImmer<DeliveryDetailUi>(initialState);
  const orderDetailId = useParams().orderDetailId || '';
  const { data } = useGetOrderDetails(orderDetailId);
  const { session } = useGlobalContext();
  const { updateDeliverySettings } = useUpdateDeliverySettings();

  useEffect(() => {
    if (!data || !session) return;
    const newUiState = orderDetailsResponseToOrderDeliveryDetail(data, session);
    setUiState(newUiState);
  }, [data, session]);

  const validateDeliveryDate = (draft: WritableDraft<DeliveryDetailUi>) => {
    draft.deliveryDateError =
      !draft.deliveryDate || !draft.deliveryDate.isValid()
        ? 'Debe seleccionar una fecha de entrega.'
        : undefined;
  };

  const validateTimes = (draft: WritableDraft<DeliveryDetailUi>) => {
    if (!draft.deliveryStartTime || !draft.deliveryEndTime) {
      draft.deliveryEndTimeError =
        'Debe seleccionar una hora de inicio y final.';
      return;
    }
    if (
      draft.deliveryStartTime.isAfter(draft.deliveryEndTime) ||
      draft.deliveryStartTime.isSame(draft.deliveryEndTime)
    ) {
      draft.deliveryEndTimeError =
        'La hora final debe ser mayor a la hora de inicio.';
      return;
    }
    draft.deliveryEndTimeError = undefined;
  };

  const calculateSaveDisabled = (draft: WritableDraft<DeliveryDetailUi>) => {
    draft.saveDisabled =
      !draft.deliveryDate ||
      !draft.deliveryStartTime ||
      !draft.deliveryEndTime ||
      !!draft.deliveryEndTimeError;
  };

  const onDateChange = (newDate: Dayjs | null) =>
    setUiState((draft) => {
      draft.deliveryDate = newDate;
      validateTimes(draft);
      validateDeliveryDate(draft);
      calculateSaveDisabled(draft);
    });

  const onStartTimeChange = (newTime: Dayjs | null) =>
    setUiState((draft) => {
      draft.deliveryStartTime = newTime;
      validateTimes(draft);
      validateDeliveryDate(draft);
      calculateSaveDisabled(draft);
    });

  const onEndTimeChange = (newTime: Dayjs | null) =>
    setUiState((draft) => {
      draft.deliveryEndTime = newTime;
      validateTimes(draft);
      validateDeliveryDate(draft);
      calculateSaveDisabled(draft);
    });

  const onNotesChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) =>
    setUiState((draft) => {
      draft.deliveryNotes = event.target.value;
      validateTimes(draft);
      validateDeliveryDate(draft);
      calculateSaveDisabled(draft);
    });

  const onSaveChanges = () => {
    updateDeliverySettings({
      orderId: orderDetailId,
      deliveryDate: uiState.deliveryDate,
      deliveryStartTime: uiState.deliveryStartTime ?? undefined,
      deliveryEndTime: uiState.deliveryEndTime ?? undefined,
      deliveryNotes: uiState.deliveryNotes,
    });
  };

  return {
    uiState,
    actions: {
      onDateChange,
      onStartTimeChange,
      onEndTimeChange,
      onNotesChange,
      onSaveChanges,
    },
  };
}
